import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import CallReceivedOutlinedIcon from "@mui/icons-material/CallReceivedOutlined";
import PhoneMissedOutlinedIcon from "@mui/icons-material/PhoneMissedOutlined";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import { useTranslation } from "react-i18next";
import { formatPhoneDisplay } from "../../utils/phone";
import { humanizeVoiceCallError } from "../../utils/voiceCallErrors";
import { useCallContext } from "../../context/CallContext";
import { useAuthContext } from "../../context/AuthContext";
import {
  subscribeCallSessionBridge,
  requestCallSessionSync,
} from "../../utils/callSessionBridge";
import AudioMessage from "./AudioMessage";
import {
  formatVoiceDurationSec,
  resolveAttendedMode,
  resolveVoiceCallTitleKey,
  voiceCallTitleIsViewer,
  showVoiceCallAsInbound,
  resolveVoiceOutcome,
  isLiveVoiceCall,
} from "./voiceCallLogDisplay";

function parseJsonMaybe(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return null;
  }
}

export function resolveVoiceCallLog(message) {
  const json = parseJsonMaybe(message?.json_whatsapp);
  const voice = json?.xpro_voice_call;
  if (voice && typeof voice === "object") return voice;
  const code = json?.xpro_log?.code;
  if (code === "voice_call") {
    return { plainOnly: true, text: message?.text || message?.mensagem || "" };
  }
  return null;
}

const STATUS_I18N = {
  initiated: "voiceCall.statusInitiated",
  ringing: "voiceCall.statusRinging",
  queued: "voiceCall.statusQueued",
  "in-progress": "voiceCall.statusInProgress",
  answered: "voiceCall.statusAnswered",
  completed: "voiceCall.statusCompleted",
  busy: "voiceCall.statusBusy",
  failed: "voiceCall.statusFailed",
  "no-answer": "voiceCall.statusNoAnswer",
  rejected: "voiceCall.statusRejected",
  "technical-drop": "voiceCall.statusTechnicalDrop",
  canceled: "voiceCall.statusCanceled",
  client_error: "voiceCall.statusClientError",
};

const OUTCOME_I18N = {
  answered: "voiceCall.outcomeAnswered",
  no_answer: "voiceCall.outcomeNoAnswer",
  rejected: "voiceCall.outcomeRejected",
  busy: "voiceCall.outcomeBusy",
  failed: "voiceCall.outcomeFailed",
  canceled: "voiceCall.outcomeCanceled",
  technical_drop: "voiceCall.outcomeTechnicalDrop",
  mailbox: "voiceCall.outcomeMailbox",
};

function friendlyPhone(raw, display) {
  if (display) return display;
  const s = String(raw || "").trim();
  if (!s) return "";
  if (s.toLowerCase().startsWith("client:")) return "Softphone";
  return formatPhoneDisplay(s) || s;
}

function resolveOutcome(voice) {
  return resolveVoiceOutcome(voice, OUTCOME_I18N);
}

function participantRoleLabel(role, t) {
  const r = String(role || "").toLowerCase();
  if (r === "customer") return t("voiceCall.roleCustomer", "Cliente");
  if (r === "waiting") return t("voiceCall.roleWaiting", "Aguardando atendente");
  return t("voiceCall.roleAgent", "Atendente");
}

function participantIcon(role) {
  const r = String(role || "").toLowerCase();
  if (r === "customer") return PersonOutlineOutlinedIcon;
  if (r === "waiting") return HourglassEmptyOutlinedIcon;
  return HeadsetMicOutlinedIcon;
}

function buildFallbackParticipants(voice) {
  const list = [];
  const customerLabel = friendlyPhone(voice?.from, voice?.fromDisplay) || "Cliente";
  list.push({ role: "customer", label: customerLabel, userId: null });
  const st = String(voice?.status || "").toLowerCase();
  if (st === "queued") {
    list.push({
      role: "waiting",
      label: "Aguardando atendente",
      userId: null,
    });
  }
  return list;
}

function resolveParticipants(voice) {
  if (Array.isArray(voice?.participants) && voice.participants.length) {
    return voice.participants;
  }
  return buildFallbackParticipants(voice);
}

function collectCallSids(session) {
  if (!session || typeof session !== "object") return [];
  const params = session.callInstance?.parameters || {};
  return [
    session.callSid,
    session.parentCallSid,
    session.childCallSid,
    params.CallSid,
    params.ParentCallSid,
  ]
    .map((s) => String(s || "").trim())
    .filter(Boolean);
}

/** Softphone local ou sessão espelhada do /popup-call (BroadcastChannel). */
function isActiveSessionOnThisCall(session, { leadId, callSid, childCallSid, from } = {}) {
  if (!session) return false;

  const sessionLead = Number(session.leadId || 0);
  const voiceLead = Number(leadId || 0);
  if (sessionLead > 0 && voiceLead > 0 && sessionLead === voiceLead) return true;

  const sessionSids = new Set(collectCallSids(session));
  const voiceSids = [callSid, childCallSid]
    .map((s) => String(s || "").trim())
    .filter(Boolean);
  if (voiceSids.some((s) => sessionSids.has(s))) return true;

  const sessionPhone = String(session.toNumber || session.fromNumber || "").replace(/\D/g, "");
  const voicePhone = String(from || "").replace(/\D/g, "");
  if (
    sessionPhone.length >= 8 &&
    voicePhone.length >= 8 &&
    (sessionPhone.endsWith(voicePhone.slice(-8)) || voicePhone.endsWith(sessionPhone.slice(-8)))
  ) {
    return true;
  }
  return false;
}

/**
 * Card de tentativa/resultado de ligação telefônica na timeline (visão do atendente).
 */
const VoiceCallLogMessage = ({ message }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const { joinLiveVoiceCall, outgoingCall, connectingToLead } = useCallContext();
  const { userData } = useAuthContext();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [remoteCallSession, setRemoteCallSession] = useState(null);

  const voice = resolveVoiceCallLog(message);

  const participants = useMemo(
    () => (voice && !voice.plainOnly ? resolveParticipants(voice) : []),
    [voice]
  );
  const liveCall = voice && !voice.plainOnly ? isLiveVoiceCall(voice) : false;
  const channelId =
    voice && !voice.plainOnly
      ? Number(voice.channelId || message?.id_origem || 0) || null
      : null;
  const leadId =
    voice && !voice.plainOnly
      ? Number(voice.leadId || message?.id_lead || 0) || null
      : null;
  const callSid =
    voice && !voice.plainOnly
      ? String(voice.callSid || message?.sid || "").trim() || null
      : null;
  const childCallSid =
    voice && !voice.plainOnly
      ? String(voice.childCallSid || "").trim() || null
      : null;
  const currentUserId = Number(userData?.id || 0) || null;

  // No /popup-call o softphone pode viver na outra janela — espelha a sessão ativa.
  useEffect(() => {
    if (!liveCall) {
      setRemoteCallSession(null);
      return undefined;
    }
    requestCallSessionSync();
    return subscribeCallSessionBridge((msg) => {
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "cleared") {
        setRemoteCallSession(null);
        return;
      }
      if (msg.type === "session") {
        setRemoteCallSession(msg.session || null);
      }
    });
  }, [liveCall]);

  const userAlreadyOnCall = useMemo(() => {
    if (!voice || voice.plainOnly) return false;
    const matchOpts = {
      leadId,
      callSid,
      childCallSid,
      from: voice.from,
    };
    if (isActiveSessionOnThisCall(outgoingCall, matchOpts)) return true;
    if (
      Number(connectingToLead) > 0 &&
      Number(leadId) > 0 &&
      Number(connectingToLead) === Number(leadId)
    ) {
      return true;
    }
    if (!outgoingCall && isActiveSessionOnThisCall(remoteCallSession, matchOpts)) {
      return true;
    }
    if (
      currentUserId &&
      participants.some(
        (p) => Number(p?.userId) === currentUserId && String(p?.role).toLowerCase() === "agent"
      )
    ) {
      return true;
    }
    return false;
  }, [
    callSid,
    childCallSid,
    connectingToLead,
    currentUserId,
    leadId,
    outgoingCall,
    participants,
    remoteCallSession,
    voice,
  ]);

  if (!voice) return null;

  if (voice.plainOnly) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
        {voice.text}
      </Typography>
    );
  }

  const outbound = String(voice.direction || "").startsWith("out");
  const attendedMode = resolveAttendedMode(voice);
  const showAsInbound = showVoiceCallAsInbound(voice, outbound);
  const actorUserId = Number(
    voice.actorUserId || message?.logMeta?.actorUserId || message?.id_usuario || 0
  ) || 0;
  const actorUserName = String(
    voice.actorUserName || message?.logMeta?.actorUserName || message?.senderName || ""
  ).trim();
  const titleIsYou = voiceCallTitleIsViewer({
    actorUserId,
    currentUserId,
    actorUserName,
  });
  const titleKey = resolveVoiceCallTitleKey(voice, outbound, { isYou: titleIsYou });
  const outcome = resolveOutcome(voice);
  const missed =
    outcome === "no_answer" ||
    outcome === "rejected" ||
    outcome === "technical_drop" ||
    outcome === "mailbox" ||
    outcome === "failed";
  const statusKey = STATUS_I18N[voice.status] || null;
  const outcomeKey = outcome ? OUTCOME_I18N[outcome] : null;
  const statusText =
    voice.displayStatusLabel ||
    voice.answerOutcomeLabel ||
    voice.statusLabel ||
    (outcomeKey
      ? t(outcomeKey, voice.status)
      : statusKey
        ? t(statusKey, voice.status)
        : voice.status) ||
    "—";

  const fromLabel = friendlyPhone(voice.from, voice.fromDisplay);
  const toLabel = friendlyPhone(voice.to, voice.toDisplay);
  const recordingUrl =
    voice.recordingUrl ||
    (message?.audio && String(message.audio).trim()) ||
    null;

  const detailRows = [];
  if (!showAsInbound) {
    if (toLabel) detailRows.push({ label: t("voiceCall.to", "Para"), value: toLabel });
    if (fromLabel && fromLabel !== "Softphone") {
      detailRows.push({ label: t("voiceCall.viaNumber", "Pelo número"), value: fromLabel });
    }
  } else {
    if (fromLabel) detailRows.push({ label: t("voiceCall.from", "De"), value: fromLabel });
    if (toLabel) detailRows.push({ label: t("voiceCall.onNumber", "No número"), value: toLabel });
  }

  const geo = voice.geo && typeof voice.geo === "object" ? voice.geo : null;
  if (geo) {
    const fromPlace = [geo.fromCity, geo.fromState, geo.fromCountry].filter(Boolean).join(", ");
    const toPlace = [geo.toCity, geo.toState, geo.toCountry].filter(Boolean).join(", ");
    if (fromPlace) {
      detailRows.push({
        label: t("voiceCall.origin", "Origem"),
        value: `${fromPlace}${geo.fromZip ? ` · ${geo.fromZip}` : ""}`,
      });
    }
    if (toPlace && toPlace !== fromPlace) {
      detailRows.push({
        label: t("voiceCall.destination", "Destino"),
        value: `${toPlace}${geo.toZip ? ` · ${geo.toZip}` : ""}`,
      });
    }
  }

  const waitLabel =
    voice.waitSec != null
      ? voice.waitLabel || formatVoiceDurationSec(voice.waitSec)
      : null;
  if (waitLabel) {
    detailRows.push({
      label: t("voiceCall.waitTime", "Tempo de espera"),
      value: waitLabel,
      emphasize: missed || outcome === "answered",
    });
  }

  if (voice.bridgeMode === "click_to_call" || Number(voice.legs) === 2) {
    detailRows.push({
      label: t("voiceCall.mode", "Modo"),
      value: t("voiceCall.clickToCallMode", "Telefone de origem → destino (2 pernas)"),
      emphasize: true,
    });
    const agentPhone = friendlyPhone(voice.agentPhone, null);
    if (agentPhone) {
      detailRows.push({
        label: t("voiceCall.originPhone", "Telefone de origem"),
        value: agentPhone,
      });
    }
  } else if (attendedMode === "queue_claim") {
    detailRows.push({
      label: t("voiceCall.howJoined", "Como entrou"),
      value: titleIsYou
        ? t("voiceCall.queueClaimHow", "Você puxou da fila de espera")
        : t("voiceCall.userQueueClaimHow", "{{name}} puxou da fila de espera", {
            name: actorUserName || "Atendente",
          }),
      emphasize: true,
    });
  } else if (attendedMode === "queue_auto") {
    detailRows.push({
      label: t("voiceCall.howJoined", "Como entrou"),
      value: t("voiceCall.queueAutoHow", "A fila atribuiu automaticamente"),
      emphasize: true,
    });
  } else if (attendedMode === "live_join") {
    detailRows.push({
      label: t("voiceCall.howJoined", "Como entrou"),
      value: t("voiceCall.liveJoinHow", "Entrou numa ligação em andamento"),
      emphasize: true,
    });
  } else if (attendedMode === "inbound_answer") {
    detailRows.push({
      label: t("voiceCall.howJoined", "Como entrou"),
      value: t("voiceCall.inboundAnswerHow", "Atendeu no softphone"),
    });
  }

  const talkLabel =
    voice.durationLabel ||
    (voice.durationSec != null && Number(voice.durationSec) >= 0
      ? formatVoiceDurationSec(voice.durationSec)
      : null);
  if (talkLabel && outcome === "answered") {
    detailRows.push({
      label: t("voiceCall.talkDuration", "Duração da conversa"),
      value: talkLabel,
    });
  } else if (talkLabel) {
    detailRows.push({
      label: t("voiceCall.lineDuration", "Duração na linha"),
      value: talkLabel,
    });
  }

  const endedBy = String(voice.endedBy || "").toLowerCase();
  const endedByText =
    voice.endedByLabel ||
    (endedBy === "user"
      ? t("voiceCall.endedByUser", "Encerrada pelo usuário")
      : endedBy === "client"
        ? t("voiceCall.endedByClient", "Encerrada pelo cliente")
        : endedBy === "system"
          ? t("voiceCall.endedBySystem", "Encerrada pelo sistema")
          : "");
  if (endedByText && (outcome === "answered" || voice.status === "completed" || voice.status === "answered")) {
    detailRows.push({ label: t("voiceCall.endedBy", "Encerrada"), value: endedByText });
  }

  const failureRaw = String(voice.failureReason || voice.failureReasonRaw || "").trim();
  const failureText =
    humanizeVoiceCallError(failureRaw, {
      status: voice.status,
      code: voice.failureCode || voice.errorCode,
    }) || failureRaw;
  const showFailure = failureText && !/^\d{3}$/.test(failureText);

  const accent =
    outcome === "rejected"
      ? theme.palette.warning.main
      : outcome === "technical_drop"
        ? theme.palette.warning.dark
        : outcome === "no_answer" ||
            outcome === "busy" ||
            outcome === "failed" ||
            outcome === "mailbox"
          ? theme.palette.error.main
          : theme.palette.primary.main;

  const TitleIcon = missed
    ? outcome === "rejected"
      ? CallEndOutlinedIcon
      : PhoneMissedOutlinedIcon
    : showAsInbound
      ? CallReceivedOutlinedIcon
      : CallMadeOutlinedIcon;

  const softphoneBusyElsewhere = Boolean(
    (outgoingCall?.callInstance || outgoingCall?.mode === "mock") && !userAlreadyOnCall
  );

  const canJoin =
    liveCall &&
    showAsInbound &&
    callSid &&
    channelId &&
    leadId &&
    !userAlreadyOnCall &&
    !softphoneBusyElsewhere &&
    typeof joinLiveVoiceCall === "function";

  const onJoin = async () => {
    if (!canJoin || joining) return;
    setJoining(true);
    setJoinError(null);
    try {
      await joinLiveVoiceCall({ channelId, leadId, callSid });
    } catch (err) {
      setJoinError(
        err?.response?.data?.message ||
          err?.message ||
          t("voiceCall.joinCallError", "Não foi possível entrar na ligação.")
      );
    } finally {
      setJoining(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 440,
        mx: "auto",
        textAlign: "left",
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(accent, 0.35),
        bgcolor: alpha(accent, theme.palette.mode === "dark" ? 0.14 : 0.05),
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <TitleIcon sx={{ fontSize: 18, color: accent }} />
        <PhoneInTalkOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
          {t(titleKey, {
            name: actorUserName || "Atendente",
            defaultValue:
              titleKey === "voiceCall.userCalledTitle"
                ? `${actorUserName || "Atendente"} ligou`
                : titleKey === "voiceCall.userQueueClaimTitle"
                  ? `${actorUserName || "Atendente"} atendeu da fila`
                  : titleKey === "voiceCall.userLiveJoinTitle"
                    ? `${actorUserName || "Atendente"} entrou na ligação`
                    : titleKey === "voiceCall.userInboundAnswerTitle"
                      ? `${actorUserName || "Atendente"} atendeu a ligação`
                      : titleKey === "voiceCall.userClickToCallTitle"
                        ? `Ligação pelo telefone de ${actorUserName || "atendente"}`
                        : titleKey === "voiceCall.queueClaimTitle"
                          ? "Você atendeu da fila"
                          : titleKey === "voiceCall.queueAutoTitle"
                            ? "Ligação da fila"
                            : titleKey === "voiceCall.liveJoinTitle"
                              ? "Você entrou na ligação"
                              : titleKey === "voiceCall.inboundAnswerTitle"
                                ? "Você atendeu a ligação"
                                : titleKey === "voiceCall.clickToCallTitle"
                                  ? "Ligação pelo seu telefone"
                                  : outbound
                                    ? "Você ligou"
                                    : "Ligação recebida",
          })}
          {statusText ? ` — ${statusText}` : ""}
        </Typography>
      </Stack>

      {participants.length > 0 ? (
        <Box sx={{ mb: detailRows.length || canJoin || showFailure || recordingUrl ? 0.75 : 0 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
            {t("voiceCall.participants", "Na ligação")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
            {participants.map((p, idx) => {
              const RoleIcon = participantIcon(p?.role);
              const isYou =
                currentUserId &&
                Number(p?.userId) === currentUserId &&
                String(p?.role).toLowerCase() === "agent";
              const roleLabel = participantRoleLabel(p?.role, t);
              const label = isYou
                ? t("voiceCall.youOnCall", "Você")
                : String(p?.label || roleLabel);
              return (
                <Chip
                  key={`${p?.role}-${p?.userId || idx}-${label}`}
                  size="small"
                  icon={<RoleIcon sx={{ fontSize: "14px !important" }} />}
                  label={`${roleLabel}: ${label}`}
                  variant="outlined"
                  sx={{
                    borderColor: alpha(accent, 0.35),
                    bgcolor: alpha(accent, theme.palette.mode === "dark" ? 0.08 : 0.04),
                    maxWidth: "100%",
                    "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      ) : null}

      {detailRows.length > 0 && (
        <Stack
          spacing={0.35}
          sx={{
            mb: showFailure || recordingUrl || canJoin ? 0.75 : 0,
          }}
        >
          {detailRows.map((row) => (
            <Stack
              key={`${row.label}-${row.value}`}
              direction="row"
              spacing={1}
              alignItems="baseline"
              sx={{ minWidth: 0 }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  minWidth: 118,
                  flexShrink: 0,
                  lineHeight: 1.45,
                }}
              >
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: row.emphasize ? "text.primary" : "text.secondary",
                  fontWeight: row.emphasize ? 600 : 400,
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {canJoin ? (
        <Box sx={{ mt: 0.5, mb: showFailure || recordingUrl ? 0.75 : 0 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={joining}
            startIcon={
              joining ? <CircularProgress size={14} color="inherit" /> : <PhoneInTalkOutlinedIcon />
            }
            onClick={() => void onJoin()}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {joining
              ? t("voiceCall.joiningCall", "Conectando…")
              : t("voiceCall.joinCall", "Entrar na ligação")}
          </Button>
          {joinError ? (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {joinError}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {userAlreadyOnCall && liveCall && !canJoin ? (
        <Typography variant="caption" sx={{ color: "success.main", display: "block", mb: 0.75 }}>
          {t("voiceCall.youOnCallHint", "Você já está nesta ligação.")}
        </Typography>
      ) : null}

      {showFailure ? (
        <Typography variant="body2" sx={{ color: "error.main", display: "block", lineHeight: 1.45 }}>
          {failureText}
        </Typography>
      ) : null}

      {recordingUrl ? (
        <Box sx={{ mt: showFailure || detailRows.length ? 1 : 0 }}>
          <Divider sx={{ mb: 1, borderColor: alpha(accent, 0.2) }} />
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
            {t("voiceCall.recording", "Gravação")}
          </Typography>
          <AudioMessage
            message={{
              ...message,
              audio: recordingUrl,
              transcription: voice.transcription || message?.transcription || "",
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default VoiceCallLogMessage;
